// Home "Top Games" hero CAROUSEL — the rotating flagship card.
//
// Spec: docs/HOME_TOP_GAMES_CAROUSEL_SPEC.md (owner, 2026-08-11).
//
//   - 5 top games rotate through ONE fixed card shell (aspect-[16/9]).
//   - Each game holds the card for 7s. Within that: its POSTER image for
//     the first 1.5s, which then SLIDES UP like a curtain to reveal that
//     game's live content underneath. Real artwork carries no overlaid text
//     and no dimming — the hero images already say the game's name.
//   - The poster replays EVERY time a game comes around — first load, every
//     loop, and every manual prev/next. The cycle repeats forever.
//   - Content per game: cricket/football/tennis = scrollable match list with
//     the shared live 1/X/2 back/lay odds rows; lucky7 = recent released
//     results; matka = today's games LIVE (poster + "COMING SOON" only while locked).
//   - Whenever a scrollable list appears it plays a scroll NUDGE (down and
//     smoothly back to the top, inside 1-2s) so the player learns it scrolls.
//   - Small-but-touchable prev/next controls, themed for light + dark.
//
// Display-only: no money or settlement logic lives here.
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import { useToast } from '@baaligames/shared-ui/lib/toast';
import { api } from '@baaligames/shared-ui/lib/api';
import { buildTables, deviceSeed, recentResults } from '../games/roulette/tables';
import { colourOf } from '../games/roulette/engine';
import Icon from '@baaligames/shared-ui/components/Icon';
import { useLobbyGames, type LobbyGame } from '../lib/lobbyGames';
import LockedGameModal from './LockedGameModal';
import LiveOddsRow from './CricketOddsRow';

/* ─── Timings (the owner's numbers) ─────────────────────────────── */
const POSTER_MS = 1500;   // poster holds 1.5s — 1s read too fast (owner)
const SLIDE_MS  = 7000;   // each game owns the card for 7 seconds
const EXTEND_MS = 15000;  // …but an engaged player gets much longer to look
const NUDGE_IN  = 250;    // nudge starts just after the list fades in
const NUDGE_BACK = 900;   // …and returns to the top — total well inside 2s
const SWIPE_MIN = 48;     // px of horizontal travel that commits to a slide change

/* ─── The 5 top games (same order as the Top Games tiles) ───────── */
type Slide = {
  id: string;
  name: string;
  emoji: string;
  emojiClass?: string;
  gradient: string;                    // poster fallback when there is no art
  poster?: string;                     // explicit wide poster
  posterBg?: string;                   // full-bleed backdrop behind a logo poster
  posterFit?: 'cover' | 'contain';
  kind: 'sport' | 'lucky7' | 'roulette' | 'matka';
  sport?: string;                      // for kind==='sport'
  accent: string;                      // header bar gradient
  tagline: string;
};

const SLIDES: Slide[] = [
  { id: 'cricket',  name: 'Cricket',    emoji: '🏏', gradient: 'from-emerald-500 to-green-800',
    poster: '/cricket_hero.png', posterFit: 'cover',
    kind: 'sport',  sport: 'cricket',  accent: 'from-red-600 to-brand-600',      tagline: 'Live Exchange' },
  // 2026-09-19 (plan M-81): the real game id, a LIVE slide (today's games with
  // their open / close countdowns and boards); the poster + "COMING SOON" words
  // stay only while the game is locked for this player.
  { id: 'matka_king', name: 'Matka King', emoji: '🍯', gradient: 'from-amber-600 to-yellow-900',
    poster: '/matka_king_hero.jpg', posterFit: 'cover',
    kind: 'matka',  accent: 'from-amber-600 to-yellow-700',                      tagline: "Today's Games" },
  { id: 'football', name: 'Football',   emoji: '⚽', gradient: 'from-green-700 to-emerald-950',
    poster: '/football_hero.jpg', posterFit: 'cover',
    kind: 'sport',  sport: 'football', accent: 'from-emerald-700 to-green-600',  tagline: 'Live Exchange' },
  { id: 'lucky7',   name: 'Lucky 7',    emoji: '❼', emojiClass: 'text-white',
    gradient: 'from-rose-600 to-red-900',
    poster: '/lucky_7_hero.jpg', posterFit: 'cover',
    kind: 'lucky7', accent: 'from-rose-700 to-red-600',                          tagline: 'Recent Results' },
  { id: 'tennis',   name: 'Tennis',     emoji: '🎾', gradient: 'from-lime-600 to-yellow-800',
    poster: '/tennis_hero.jpg', posterFit: 'cover',
    kind: 'sport',  sport: 'tennis',   accent: 'from-lime-700 to-yellow-600',    tagline: 'Live Exchange' },
  { id: 'roulette', name: 'Roulette',   emoji: '🎡', gradient: 'from-emerald-700 to-red-900',
    kind: 'roulette', accent: 'from-emerald-700 to-red-700',                     tagline: 'Recent Numbers' },
];

/* ─── Matka King slide helpers (2026-09-19) — the same status words the game
       page uses, so the home card and the game never disagree. ─────── */
type MkLeg = { time: string | null; deadlineMs: number | null; status: string; open: boolean; withinWindow: boolean };
type MkMarket = {
  id: number; kind: 'double' | 'single'; name: string; runsToday: boolean; holiday: boolean;
  openTime?: string; closeTime?: string; legs?: { open: MkLeg; close: MkLeg };
  result?: { openPana: string | null; openDigit: string | null; closePana: string | null; closeDigit: string | null; red: boolean } | null;
  slots?: Array<MkLeg & { slot: number; result: { pana: string; digit: string } | null }>;
};
const mkHhmm = (t?: string | null) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};
const mkLeft = (ms: number | null, now: number) => {
  const d = Math.max(0, (ms ?? 0) - now);
  const h = Math.floor(d / 3600000), m = Math.floor((d % 3600000) / 60000), s = Math.floor((d % 60000) / 1000);
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
};
const MK_TONE_ON = 'bg-amber-500 text-white';
const MK_TONE_OFF = 'bg-arena-200 text-arena-600 dark:bg-white/10 dark:text-neon-200/60';
const MK_TONE_DONE = 'bg-emerald-600 text-white';
function matkaStatus(m: MkMarket, now: number): { chip: string; sub: string; board: string; tone: string } {
  if (!m.runsToday || m.holiday) {
    return { chip: m.holiday ? 'Holiday' : 'Closed', sub: 'Not today', board: m.kind === 'double' ? '***-**-***' : '—', tone: MK_TONE_OFF };
  }
  if (m.kind === 'double') {
    const r = m.result;
    const board = r ? `${r.openPana ?? '***'}-${r.openDigit ?? '*'}${r.closeDigit ?? '*'}-${r.closePana ?? '***'}` : '***-**-***';
    const L = m.legs;
    const times = `Open ${mkHhmm(m.openTime)} · Close ${mkHhmm(m.closeTime)}`;
    if (L?.open.status === 'pending' && L.open.open && L.open.withinWindow) return { chip: 'Open', sub: `Open closes in ${mkLeft(L.open.deadlineMs, now)}`, board, tone: MK_TONE_ON };
    if (L?.close.status === 'pending' && L.close.open && L.close.withinWindow) return { chip: 'Close', sub: `Close closes in ${mkLeft(L.close.deadlineMs, now)}`, board, tone: MK_TONE_ON };
    if (L && L.close.status !== 'pending') return { chip: 'Declared', sub: times, board, tone: MK_TONE_DONE };
    if (L && L.open.status !== 'pending') return { chip: 'Open done', sub: `Close at ${mkHhmm(m.closeTime)}`, board, tone: MK_TONE_OFF };
    return { chip: 'Later', sub: times, board, tone: MK_TONE_OFF };
  }
  const slots = m.slots || [];
  const live = slots.find((s) => s.status === 'pending' && s.open && s.withinWindow);
  const done = slots.filter((s) => s.result);
  const last = done[done.length - 1];
  const board = last?.result ? `${last.result.pana}-${last.result.digit}` : '—';
  if (live) return { chip: 'Live', sub: `Slot ${live.slot} closes in ${mkLeft(live.deadlineMs, now)}`, board, tone: MK_TONE_ON };
  const next = slots.find((s) => s.status === 'pending' && s.open);
  if (next) return { chip: 'Next', sub: `Slot ${next.slot} at ${mkHhmm(next.time)} · ${done.length}/${slots.length} declared`, board, tone: MK_TONE_OFF };
  return { chip: 'Declared', sub: `${done.length}/${slots.length} slots declared`, board, tone: MK_TONE_DONE };
}

/* ─── Tiny module-level cache so re-visiting a slide mid-cycle does
       not re-hit the API every 7 seconds. ─────────────────────────── */
/* Roulette's slide shows the SAME numbers its own lobby shows: the first
 * room's clock-derived recent results (plus the player's watched-results
 * overlay). One fiction everywhere — carousel, lobby card and in-game Record
 * can never disagree, and the strip is always fresh because the virtual
 * clock never stops. No API call needed. */
const CACHE_TTL = 30_000;
const cache: Record<string, { at: number; v: any }> = {};
async function cachedGet<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const hit = cache[key];
  if (hit && Date.now() - hit.at < CACHE_TTL) return hit.v as T;
  const v = await fetcher();
  cache[key] = { at: Date.now(), v };
  return v;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/* ─── Poster layer — real art if we have it, else a designed
       gradient + big emoji so every game still gets a poster. ────── */
function Poster({ slide, src, onImgError, locked, t }: {
  slide: Slide; src?: string; onImgError: () => void; locked: boolean; t: (k: string) => string;
}) {
  const cover = (slide.posterFit ?? 'contain') === 'cover';
  return (
    <div className="absolute inset-0">
      {/* Gradient always sits underneath — it is the designed backdrop AND the
          fallback for games we have no artwork for. */}
      <div className={`absolute inset-0 bg-gradient-to-br ${slide.gradient}`} aria-hidden />
      {/* Optional full-bleed backdrop photo (e.g. the Lucky 7 table), so a
          logo-style poster still reads as a real hero card and not a sticker. */}
      {/* Full-bleed backdrop. Only used when a game has no wide poster of its
          own — a future game can get a hero simply by dropping
          `/games/<id>/hero.jpg` into player-frontend/public/, no code change.
          If the file is not there the image just hides itself. */}
      {(slide.posterBg || !src) && (
        <img src={slide.posterBg ?? `/games/${slide.id}/hero.jpg`} alt="" aria-hidden
             className="absolute inset-0 w-full h-full object-cover opacity-90"
             onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
      )}
      {src ? (
        cover ? (
          <img src={src} alt={slide.name} onError={onImgError}
               className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <img src={src} alt={slide.name} onError={onImgError}
               className="absolute inset-0 m-auto h-[64%] w-auto max-w-[74%] object-contain drop-shadow-2xl" />
        )
      ) : (
        /* No artwork for this game — render a DESIGNED hero instead of a bare
           emoji: soft spotlight, oversized mark, and the name carried below. */
        <>
          <div className="absolute inset-0" aria-hidden
               style={{ background: 'radial-gradient(120% 90% at 50% 30%, rgba(255,255,255,.30), transparent 60%)' }} />
          <span aria-hidden
                className={`absolute inset-0 grid place-items-center text-[76px] sm:text-[92px]
                            drop-shadow-[0_10px_28px_rgba(0,0,0,.55)] ${slide.emojiClass || ''}`}>
            {slide.emoji}
          </span>
        </>
      )}
      {/* NO text and NO dimming over real artwork (owner, 2026-08-11): each
          hero image already carries its own game name, and a scrim added to
          make overlaid text legible only made the picture look washed out.
          Text + scrim are therefore ONLY drawn for the designed fallback
          poster of a game we have no artwork for. */}
      {!src && (
        <>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/25" aria-hidden />
          <div className="relative p-4 flex flex-col justify-between h-full">
            <div className="flex justify-between items-start">
              <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-extrabold
                               uppercase tracking-widest text-white shadow-lg border
                               ${slide.kind === 'matka' && locked
                                 ? 'bg-amber-500/90 border-amber-300'
                                 : 'bg-red-500/90 border-red-400'}`}>
                {slide.kind === 'matka' && locked
                  ? <>{t('home.comingSoon')}</>
                  : <><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> {slide.tagline}</>}
              </div>
            </div>
            <div className="mt-auto self-start">
              <div className="text-white font-display font-black text-2xl uppercase tracking-wide drop-shadow-lg">
                {slide.name}
              </div>
            </div>
          </div>
        </>
      )}
      {/* A locked game still has to say so — functional, not decoration. */}
      {locked && (
        <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full
                        bg-black/70 text-white text-[11px] font-bold uppercase tracking-wider shadow-lg">
          <Icon name="lock" size={13} filled /> Locked
        </div>
      )}
    </div>
  );
}

export default function TopGamesCarousel(
  { onActiveChange }: { onActiveChange?: (id: string, dir?: number) => void } = {},
) {
  const { data } = useLobbyGames();
  const nav = useNavigate();
  const t = useT();
  const { push } = useToast();

  const [lockedModal, setLockedModal] = useState<LobbyGame | null>(null);
  const [idx, setIdx] = useState(0);
  // Which slide's list has been revealed. Derived PER-SLIDE (rather than a
  // plain 'poster'|'content' flag) so a new slide is never rendered in the
  // raised state for even one frame — that was making the incoming poster
  // animate DOWNWARDS into place instead of simply already being there.
  const [contentFor, setContentFor] = useState<string | null>(null);
  const [posterFailed, setPosterFailed] = useState<Record<string, boolean>>({});
  const [matches, setMatches] = useState<any[]>([]);
  const [oddsState, setOddsState] = useState<Record<string, any>>({});
  const [l7, setL7] = useState<{ total: number; mult: number }[]>([]);
  const [rlt, setRlt] = useState<number[]>([]);
  const [mk, setMk] = useState<MkMarket[]>([]);          // Matka: today's games
  const [mkOffset, setMkOffset] = useState(0);             // serverMs − Date.now()
  const [mkNow, setMkNow] = useState(() => Date.now());    // 1 s clock for the countdowns
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // Engagement: hovering or dragging HOLDS the card; scrolling/tapping/swiping
  // re-arms the timer at the longer EXTEND_MS so the player can finish looking.
  const [hover, setHover] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [tick, setTick] = useState(0);
  const [dragDx, setDragDx] = useState(0);
  const engagedRef = useRef(false);
  const nudgeUntilRef = useRef(0);   // ignore the nudge's own programmatic scroll
  const dragRef = useRef<{ x: number; y: number; active: boolean; claimed: boolean } | null>(null);
  // The live delta lives in a REF — state alone can still be uncommitted when
  // the final move and the release land in the same React batch.
  const dragDxRef = useRef(0);
  const swallowClickRef = useRef(false);
  const wheelAccRef = useRef(0);
  const wheelLockRef = useRef(false);
  const pendingDirRef = useRef(0);   // direction of a player-driven slide change
  // Big on-card arrow shown while the player swipes and for a beat after any
  // player-driven change, so the gesture is unmistakably acknowledged.
  const [flashDir, setFlashDir] = useState(0);

  const bump = useCallback(() => { engagedRef.current = true; setTick((t) => t + 1); }, []);

  // Hidden games (absent from the lobby payload) drop out of the rotation —
  // Matka King included, now that it is a real lobby game (matka_king).
  const visible = data?.games
    ? SLIDES.filter((s) => data.games.some((g) => g.id === s.id))
    : SLIDES;
  const n = visible.length;
  const safeIdx = n ? idx % n : 0;
  const slide = visible[safeIdx] ?? SLIDES[0];

  // True once THIS slide's 1.5s poster has elapsed. Because it is keyed to
  // slide.id, switching games flips it false in the same render — the new
  // poster is therefore already in place, never sliding down into view.
  const showContent = contentFor === slide.id;

  const lobbyGame = data?.games.find((g) => g.id === slide.id);
  const hasDevGrant = !!data?.myDevGrants?.includes(slide.id);
  const locked = !!lobbyGame?.isLocked && !hasDevGrant;
  // Matka King wears the "COMING SOON" words (not the generic lock body) while
  // it is locked for this player — the owner's wording for a game not yet open.
  const soonNow = slide.kind === 'matka' && locked;

  /* ── The cycle: poster 1s → content, whole slide 7s, then advance.
        Re-runs on every idx change, so prev/next restarts from the
        poster and the loop replays the poster every time around. ── */
  // Tell the Top Games tile row which game is on screen, so it can point at it.
  // We also hand over the direction of a PLAYER-driven move (swipe / wheel /
  // prev-next) so the caret can travel that way — auto-rotation reports 0, so
  // the travel animation only ever fires when the player caused it.
  useEffect(() => {
    onActiveChange?.(slide.id, pendingDirRef.current);
    pendingDirRef.current = 0;
  }, [slide.id, onActiveChange]);

  useEffect(() => {
    engagedRef.current = false;      // a fresh slide starts on the plain 7s clock
    const id = slide.id;
    const toContent = setTimeout(() => setContentFor(id), POSTER_MS);
    return () => clearTimeout(toContent);
  }, [safeIdx, slide.id]);

  /* ── Auto-advance, but give an interested player time to look:
        · hovering or dragging HOLDS the card indefinitely
        · scrolling the list, tapping, or swiping re-arms at EXTEND_MS
        Declared AFTER the poster effect so engagedRef is reset first. ── */
  useEffect(() => {
    if (hover || dragging || n <= 1) return;
    const ms = engagedRef.current ? EXTEND_MS : SLIDE_MS;
    const timer = setTimeout(() => setIdx((i) => (i + 1) % n), ms);
    return () => clearTimeout(timer);
  }, [safeIdx, n, tick, hover, dragging]);

  /* ── Data for the ACTIVE slide only. ── */
  useEffect(() => {
    let dead = false;
    setMatches([]); setL7([]); setRlt([]); setMk([]);
    if (locked) return;
    if (slide.kind === 'sport' && slide.sport) {
      const sport = slide.sport;
      cachedGet(`m:${sport}`, () =>
        api.get<{ success: boolean; matches: any[] }>(`/cricket/matches?sport=${sport}`))
        .then((r) => { if (!dead) setMatches(r?.matches || []); })
        .catch(() => {});
    } else if (slide.kind === 'lucky7') {
      // History + the LIVE payout config, so the X2/X3 badge always states the
      // real configured multiplier rather than a hardcoded guess.
      Promise.all([
        cachedGet('l7', () => api.get<{ rows: any[] }>('/lucky7/history')),
        cachedGet('l7cfg', () => api.get<any>('/lucky7/config')).catch(() => null),
      ])
        .then(([h, cfg]) => {
          if (dead) return;
          const pay = cfg?.payouts || {};
          const multFor = (total: number) =>
            Number(total === 7 ? (pay['7'] ?? 3) : total < 7 ? (pay['2to6'] ?? 2) : (pay['8to12'] ?? 2));
          const rows = (h?.rows || []).slice(0, 24);
          setL7(rows
            .map((x) => (x?.dice?.[0] ?? 0) + (x?.dice?.[1] ?? 0))
            .filter((total: number) => total > 0)
            .map((total: number) => ({ total, mult: multFor(total) })));
        })
        .catch(() => {});
    }
    else if (slide.kind === 'matka') {
      // Today's games with their live status — the same endpoint the game page
      // polls, so the card and the game never disagree.
      cachedGet('mk', () => api.get<{ date: string; serverMs: number; markets: MkMarket[] }>('/matka_king/markets'))
        .then((r) => { if (dead) return; setMk(r?.markets || []); if (r?.serverMs) setMkOffset(r.serverMs - Date.now()); })
        .catch(() => {});
    }
    else if (slide.kind === 'roulette') {
      // Pure client math (see the note above SLIDES) — derive, never fetch.
      try {
        const t0 = buildTables(deviceSeed())[0];
        setRlt(recentResults(t0, Date.now(), 23));
      } catch { /* a broken localStorage just means an empty strip */ }
    }
    return () => { dead = true; };
  }, [slide.id, slide.kind, slide.sport, locked]);

  // Live first, then upcoming; completed dropped.
  const list = [
    ...matches.filter((m) => m.status === 'in-play'),
    ...matches.filter((m) => m.status === 'upcoming'),
  ];
  const hasLive = matches.some((m) => m.status === 'in-play');

  /* ── Odds poll — only while THIS slide is a sport with a live match. ── */
  useEffect(() => {
    if (slide.kind !== 'sport' || !hasLive || locked) return;
    let dead = false;
    const poll = async () => {
      try {
        const r = await api.get<{ success: boolean; state: any }>('/cricket/state');
        if (!dead && r?.state) setOddsState(r.state);
      } catch { /* keep last good */ }
    };
    poll();
    const timer = setInterval(poll, 2000);
    return () => { dead = true; clearInterval(timer); };
  }, [slide.kind, hasLive, locked]);

  /* ── Matka countdowns — a 1 s clock only while THIS slide is Matka. ── */
  useEffect(() => {
    if (slide.kind !== 'matka' || locked) return;
    const timer = setInterval(() => setMkNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [slide.kind, locked]);

  const hasList = !locked && ((slide.kind === 'sport' && list.length > 0) || (slide.kind === 'lucky7' && l7.length > 0)
    || (slide.kind === 'roulette' && rlt.length > 0) || (slide.kind === 'matka' && mk.length > 0));

  /* ── Scroll nudge — every single time a scrollable list appears. ── */
  useEffect(() => {
    if (!showContent || !hasList || prefersReducedMotion()) return;
    const el = scrollerRef.current;
    if (!el) return;
    // Mark this window as OUR scroll so it does not count as player engagement.
    nudgeUntilRef.current = performance.now() + NUDGE_IN + NUDGE_BACK + 500;
    const down = setTimeout(() => {
      const max = el.scrollHeight - el.clientHeight;
      if (max <= 4) return;
      el.scrollTo({ top: Math.min(56, max), behavior: 'smooth' });
    }, NUDGE_IN);
    const back = setTimeout(() => el.scrollTo({ top: 0, behavior: 'smooth' }), NUDGE_IN + NUDGE_BACK);
    return () => { clearTimeout(down); clearTimeout(back); };
  }, [showContent, hasList, slide.id]);

  // Every player-driven move goes through here, so this is where we record the
  // direction the caret should travel.
  const go = useCallback((dir: 1 | -1) => {
    if (!n) return;
    pendingDirRef.current = dir;
    setFlashDir(dir);
    setIdx((i) => (((i % n) + dir) + n) % n);
  }, [n]);

  // Clear the acknowledgement arrow shortly after it appears.
  useEffect(() => {
    if (!flashDir) return;
    const tm = setTimeout(() => setFlashDir(0), 520);
    return () => clearTimeout(tm);
  }, [flashDir]);

  /* ── Player scrolling the list = "I'm reading this" → more time. ── */
  const onListScroll = useCallback(() => {
    if (performance.now() < nudgeUntilRef.current) return;   // that was the nudge
    bump();
  }, [bump]);

  /* ── Horizontal drag/swipe drives the slider; vertical is left alone so
        the list still scrolls natively (touch-action: pan-y). ── */
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    dragRef.current = { x: e.clientX, y: e.clientY, active: true, claimed: false };
    bump();
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d?.active) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.claimed) {
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        d.claimed = true;
        setDragging(true);
        swallowClickRef.current = true;    // a swipe must not also open the game
      } else if (Math.abs(dy) > 10) {
        d.active = false;                  // vertical → hand it back to the list
        return;
      }
    }
    if (d.claimed) { dragDxRef.current = dx; setDragDx(dx); }
  };
  const endDrag = () => {
    const d = dragRef.current;
    const dx = dragDxRef.current;
    dragRef.current = null;
    dragDxRef.current = 0;
    if (d?.claimed) {
      if (Math.abs(dx) > SWIPE_MIN) go(dx < 0 ? 1 : -1);
      setTimeout(() => { swallowClickRef.current = false; }, 60);
    }
    setDragDx(0);
    setDragging(false);
  };

  /* ── Trackpad / horizontal wheel behaves like a swipe. ── */
  const onWheel = (e: React.WheelEvent) => {
    // A wheel event is ALWAYS the player (the nudge never produces one), so it
    // counts as engagement even when it is vertical list scrolling.
    bump();
    if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;    // vertical → list scroll
    if (wheelLockRef.current) return;
    wheelAccRef.current += e.deltaX;
    if (Math.abs(wheelAccRef.current) > 60) {
      go(wheelAccRef.current > 0 ? 1 : -1);
      wheelAccRef.current = 0;
      wheelLockRef.current = true;
      setTimeout(() => { wheelLockRef.current = false; }, 400);
    }
  };

  function open(e: React.MouseEvent, matchId?: string) {
    e.preventDefault();
    e.stopPropagation();
    if (swallowClickRef.current) return;   // that gesture was a swipe, not a tap
    bump();                                // a tap = interest → hold the card longer
    if (soonNow) { push('info', `${slide.name} — ${t('home.comingSoon')}`); return; }
    if (locked) { if (lobbyGame) setLockedModal(lobbyGame); return; }
    nav(matchId ? `/games/${slide.id}/${matchId}` : `/games/${slide.id}`);
  }

  const posterSrc = posterFailed[slide.id]
    ? undefined
    : (slide.poster || lobbyGame?.logoPath || `/games/${slide.id}/Logo.png`);


  return (
    <>
      <section
        className="relative overflow-hidden rounded-3xl border border-arena-200/60 dark:border-neon-400/20
                   shadow-xl aspect-[16/10] sm:aspect-[16/9] bg-arena-100 dark:bg-arena-800"
        aria-roledescription="carousel"
        aria-label="Top games"
        style={{ touchAction: 'pan-y' }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => { setHover(false); endDrag(); }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onWheel={onWheel}
      >
      {/* Everything except the controls rides the swipe. */}
      <div
        className="absolute inset-0"
        style={{
          transform: dragDx ? `translateX(${dragDx * 0.55}px)` : undefined,
          transition: dragging ? 'none' : 'transform 260ms cubic-bezier(.22,.61,.36,1)',
        }}
      >
        {/* ── POSTER layer — sits ON TOP and SLIDES UP like a curtain to
               reveal the list beneath (owner, 2026-08-11). Fading was wrong:
               sliding reads as "revealing what is behind". `key` remounts it
               per slide so a new game's poster starts already down instead of
               animating back into place. ── */}
        <button
          key={slide.id}
          type="button"
          onClick={(e) => open(e)}
          aria-label={soonNow ? `${slide.name} — ${t('home.comingSoon')}` : `Open ${slide.name}`}
          className={`absolute inset-0 z-10 block text-left will-change-transform
                      transition-transform duration-[650ms] ease-[cubic-bezier(.65,0,.35,1)]
                      motion-reduce:transition-none
                      ${showContent ? '-translate-y-full pointer-events-none' : 'translate-y-0'}`}
        >
          <Poster slide={slide} src={posterSrc} locked={locked} t={t}
                  onImgError={() => setPosterFailed((p) => ({ ...p, [slide.id]: true }))} />
        </button>

        {/* ── CONTENT layer — same card, same size, scrollable ── */}
        <div className={`absolute inset-0 flex flex-col bg-white dark:bg-arena-800
                         ${showContent ? '' : 'pointer-events-none'}`}>
          {/* Header — game name + what this list is + a way in. */}
          <button
            type="button"
            onClick={(e) => open(e)}
            className={`shrink-0 flex items-center justify-between px-3 py-1 sm:py-1.5 text-white bg-gradient-to-r ${slide.accent}`}
          >
            <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider">
              <span className={slide.emojiClass}>{slide.emoji}</span>
              {slide.name} · {slide.tagline}
            </span>
            {!soonNow && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider">
                {t('home.viewAll')} <Icon name="arrow_forward" size={12} />
              </span>
            )}
          </button>

          {/* Body */}
          {soonNow ? (
            /* MATKA — locked for this player. Poster, then just the big words. */
            <div className="flex-1 min-h-0 grid place-items-center px-4 text-center">
              <div>
                <div className="font-display font-black uppercase tracking-widest leading-none
                                text-[30px] sm:text-4xl bg-gradient-to-r from-amber-500 to-yellow-600
                                dark:from-amber-300 dark:to-yellow-400 bg-clip-text text-transparent">
                  {t('home.comingSoon')}
                </div>
                <div className="mt-1.5 text-[11px] font-bold uppercase tracking-[.25em] text-arena-500 dark:text-neon-200/60">
                  {slide.name}
                </div>
              </div>
            </div>
          ) : locked ? (
            <div className="flex-1 min-h-0 grid place-items-center px-4 text-center">
              <div className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider text-arena-500 dark:text-neon-200/60">
                <Icon name="lock" size={16} filled /> {slide.name}
              </div>
            </div>
          ) : slide.kind === 'matka' ? (
            /* MATKA KING — today's games, live: which session is open right now
               and for how long, the board once declared. Tap any row → the game. */
            <div ref={scrollerRef} onScroll={onListScroll}
                 className="relative flex-1 min-h-0 overflow-y-auto px-2.5 py-2 pb-8
                            [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5"
                 style={{ background: 'radial-gradient(120% 80% at 50% 0%, rgba(180,83,9,.22), transparent 70%)' }}>
              {mk.length === 0 ? (
                <div className="h-full grid place-items-center text-[11px] font-bold uppercase tracking-wider text-arena-400 dark:text-neon-200/50">
                  {t('home.noResultsYet')}
                </div>
              ) : (
                <div className="space-y-1.5 pt-0.5">
                  {mk.map((m) => {
                    const s = matkaStatus(m, mkNow + mkOffset);
                    return (
                      <button key={m.id} type="button" onClick={(e) => open(e)}
                              className="w-full flex items-center gap-2 rounded-xl px-2.5 py-1.5 text-left
                                         bg-white/75 dark:bg-white/10 ring-1 ring-amber-900/10 dark:ring-white/10 active:scale-[.99]">
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] font-black truncate text-arena-900 dark:text-white">{m.name}</div>
                          <div className="text-[10px] text-arena-500 dark:text-neon-200/60 truncate">{s.sub}</div>
                        </div>
                        <div className={`font-mono font-black text-[13px] tracking-wider tabular-nums
                                         ${m.result?.red ? 'text-red-600 dark:text-red-400' : 'text-amber-800 dark:text-amber-300'}`}>
                          {s.board}
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-wider ${s.tone}`}>{s.chip}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : slide.kind === 'lucky7' ? (
            /* LUCKY 7 — the recent released results, in the game's own
               gold-on-maroon chip language (7 highlighted).
               NOTE: this dense result-board IS the design the owner wants.
               A "cleaner" rewrite (one big last result + a thin history strip +
               a single payout line) was rejected outright — it lost the board
               feel and dropped the per-chip X2/X3 badges the owner had asked
               for. Do NOT redesign this again without an explicit request. */
            <div ref={scrollerRef} onScroll={onListScroll}
                 className="relative flex-1 min-h-0 overflow-y-auto px-2.5 py-2 pb-8
                            [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5"
                 style={{ background: 'radial-gradient(120% 80% at 50% 0%, rgba(128,16,0,.28), transparent 70%)' }}>
              {l7.length === 0 ? (
                <div className="h-full grid place-items-center text-[11px] font-bold uppercase tracking-wider text-arena-400 dark:text-neon-200/50">
                  {t('home.noResultsYet')}
                </div>
              ) : (
                <div className="grid grid-cols-6 gap-x-2 gap-y-2.5 pt-1 pr-0.5">
                  {/* NEXT round — the pending slot, exactly like the in-game
                      strip. Makes it obvious which end is the newest. */}
                  <div title="Next result" aria-label="Next result"
                       className="relative h-9 rounded-lg border border-[#fff0a8] flex items-center justify-center
                                  font-black text-[15px] animate-pulse text-[#4d0800]
                                  bg-gradient-to-b from-[#ffe985] via-[#ffd200] to-[#d68307]
                                  shadow-[0_2px_6px_rgba(0,0,0,.5),0_0_12px_rgba(255,210,0,.5),inset_0_1px_0_rgba(255,255,255,.55)]">
                    ?
                  </div>
                  {l7.map((r, i) => {
                    const seven = r.total === 7;
                    const mult = Number.isInteger(r.mult) ? r.mult : Number(r.mult.toFixed(1));
                    return (
                      <div key={i}
                           title={`${r.total} — pays X${mult}`}
                           className={`relative h-9 rounded-lg border flex items-center justify-center
                             font-black text-[15px] tabular-nums transition-transform
                             ${seven
                               ? 'bg-gradient-to-b from-[#a01605] via-[#801000] to-[#4d0800] border-[#ffe103] text-[#ffe985] shadow-[0_2px_6px_rgba(0,0,0,.6),0_0_12px_rgba(255,225,3,.35),inset_0_1px_0_rgba(255,255,255,.28)]'
                               : 'bg-gradient-to-b from-[#4a080e] via-[#2e0509] to-[#150002] border-[#ffe103]/45 text-[#ffde03] shadow-[0_2px_5px_rgba(0,0,0,.55),inset_0_1px_0_rgba(255,255,255,.12)]'}
                             ${i === 0 ? 'ring-2 ring-[#ffe103] ring-offset-1 ring-offset-[#1b0206] shadow-[0_0_14px_rgba(255,225,3,.75)] scale-[1.04]' : ''}`}>
                        {r.total}
                        {/* Payout multiplier — gold circular badge tucked into the
                            top-right so it never fights the result itself. */}
                        <span aria-hidden
                              className="absolute -top-1 -right-1 h-4 min-w-[17px] px-[3px] rounded-full
                                         bg-gradient-to-b from-[#fff3b0] via-[#ffe103] to-[#e0a106] text-[#3d0600]
                                         text-[8px] font-black leading-none grid place-items-center
                                         shadow-[0_1px_3px_rgba(0,0,0,.55)] ring-1 ring-[#3d0600]/45">
                          X{mult}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : slide.kind === 'roulette' ? (
            /* ROULETTE — the wheel's recent numbers in the Record rail's own
               language: red/black discs, green zero, newest ringed gold, and a
               pulsing gold slot for the round being spun. Same visual grammar
               as the Lucky 7 board above so the carousel reads as one family. */
            <div ref={scrollerRef} onScroll={onListScroll}
                 className="relative flex-1 min-h-0 overflow-y-auto px-2.5 py-2 pb-8
                            [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5"
                 style={{ background: 'radial-gradient(120% 80% at 50% 0%, rgba(4,64,44,.35), transparent 70%)' }}>
              {rlt.length === 0 ? (
                <div className="h-full grid place-items-center text-[11px] font-bold uppercase tracking-wider text-arena-400 dark:text-neon-200/50">
                  {t('home.noResultsYet')}
                </div>
              ) : (
                <div className="grid grid-cols-6 gap-x-2 gap-y-2.5 pt-1 pr-0.5">
                  <div title="Next spin" aria-label="Next spin"
                       className="relative h-9 w-9 mx-auto rounded-full border border-[#fff0a8] flex items-center justify-center
                                  font-black text-[15px] animate-pulse text-[#3d2a00]
                                  bg-gradient-to-b from-[#ffe985] via-[#ffd200] to-[#d68307]
                                  shadow-[0_2px_6px_rgba(0,0,0,.5),0_0_12px_rgba(255,210,0,.5),inset_0_1px_0_rgba(255,255,255,.55)]">
                    ?
                  </div>
                  {rlt.map((n, i) => {
                    const c = colourOf(n);
                    const face = c === 'red'
                      ? 'bg-gradient-to-b from-[#e0483e] via-[#b71c1c] to-[#7f1210] border-[#ffb4a8]/60'
                      : c === 'green'
                        ? 'bg-gradient-to-b from-[#34d98b] via-[#0f9d58] to-[#0a6b3c] border-[#b9ffdd]/60'
                        : 'bg-gradient-to-b from-[#3a3f46] via-[#17191d] to-[#000] border-white/25';
                    return (
                      <div key={i} title={`${n} — ${c}`}
                           className={`relative h-9 w-9 mx-auto rounded-full border flex items-center justify-center
                             font-black text-[14px] tabular-nums text-white
                             shadow-[0_2px_5px_rgba(0,0,0,.55),inset_0_1px_0_rgba(255,255,255,.25)]
                             ${face}
                             ${i === 0 ? 'ring-2 ring-[#ffe103] ring-offset-1 ring-offset-[#06251a] shadow-[0_0_14px_rgba(255,225,3,.7)] scale-[1.06]' : ''}`}>
                        {n}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            /* CRICKET / FOOTBALL / TENNIS — match list + live odds rows. */
            <div ref={scrollerRef} onScroll={onListScroll}
                 className="flex-1 min-h-0 overflow-y-auto px-2 py-0.5 sm:py-1.5 pb-8 space-y-[3px] sm:space-y-1.5
                            [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5">
              {list.length === 0 ? (
                <div className="h-full grid place-items-center text-[11px] font-bold uppercase tracking-wider text-arena-400 dark:text-neon-200/50">
                  {t('home.noMatches')}
                </div>
              ) : list.map((m) => {
                const t1 = m.team_a || (m.title || '').replace(/ \([^)]+\)/g, '').split(' vs ')[0] || 'Team 1';
                const t2 = m.team_b || (m.title || '').replace(/ \([^)]+\)/g, '').split(' vs ')[1] || 'Team 2';
                const live = m.status === 'in-play';
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={(e) => open(e, m.id)}
                    className="w-full text-left rounded-lg bg-arena-50 dark:bg-arena-700/50
                               border border-arena-200/60 dark:border-white/5 px-2 sm:px-2.5 py-[3px] sm:py-1.5
                               active:scale-[.99] transition"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-[11px] sm:text-[12px] leading-tight text-arena-900 dark:text-white truncate">
                        {t1} <span className="text-arena-400">v</span> {t2}
                      </span>
                      {live ? (
                        <span className="shrink-0 inline-flex items-center gap-1 text-[8.5px] font-black uppercase text-red-600 dark:text-red-400">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live
                        </span>
                      ) : (
                        <span className="shrink-0 text-[8.5px] font-bold uppercase tracking-wider text-arena-400 dark:text-neon-200/50">
                          {m.start_time ? new Date(m.start_time).toLocaleDateString([], { day: '2-digit', month: 'short' }) : 'TBC'}
                        </span>
                      )}
                    </div>
                    <LiveOddsRow state={oddsState[m.id]} team1={t1} team2={t2} />
                  </button>
                );
              })}
            </div>
          )}
        </div>

      </div>

        {/* ── SWIPE ACKNOWLEDGEMENT — a big arrow on the side the player is
               heading toward. It grows with the drag and flashes on commit
               (and on prev/next), so the player can SEE that their gesture
               moved the slider and which way it went. ── */}
        {(dragging && Math.abs(dragDx) > 8) || flashDir ? (() => {
          const dir = dragging && Math.abs(dragDx) > 8 ? (dragDx < 0 ? 1 : -1) : flashDir;
          const strength = dragging && Math.abs(dragDx) > 8
            ? Math.min(1, Math.abs(dragDx) / 60)
            : 1;
          return (
            <div className={`pointer-events-none absolute inset-y-0 z-30 flex items-center px-2
                             ${dir > 0 ? 'right-0' : 'left-0'}`}>
              <span className="grid place-items-center h-14 w-14 rounded-full text-white
                               bg-black/70 backdrop-blur shadow-2xl ring-2 ring-white/70
                               transition-transform"
                    style={{ opacity: strength, transform: `scale(${0.75 + strength * 0.35})` }}>
                <Icon name={dir > 0 ? 'chevron_right' : 'chevron_left'} size={36} />
              </span>
            </div>
          );
        })() : null}

        {/* ── SLIDER CONTROLS — small, themed, properly touchable (40px),
               pinned bottom-right over both phases. ── */}
        <div className="absolute bottom-1 right-1 z-20 flex items-center gap-0.5">
          {/* Position dots. They sit over BOTH a dark poster and the light
              content card, so they carry their own legibility pill — the
              same treatment as the arrows. */}
          <div className="flex items-center gap-1 px-2 py-1 mr-0.5 rounded-full backdrop-blur
                          bg-white/75 dark:bg-arena-900/70 shadow-sm
                          border border-arena-300/60 dark:border-neon-400/25" aria-hidden>
            {visible.map((s, i) => (
              <span key={s.id}
                    className={`block h-1 rounded-full transition-all duration-300
                      ${i === safeIdx
                        ? 'w-3 bg-arena-700 dark:bg-neon-200'
                        : 'w-1 bg-arena-400/60 dark:bg-white/30'}`} />
            ))}
          </div>
          {([['chevron_left', -1], ['chevron_right', 1]] as const).map(([icon, dir]) => (
            <button
              key={icon}
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); go(dir as 1 | -1); }}
              aria-label={dir === -1 ? 'Previous game' : 'Next game'}
              className="h-10 w-10 grid place-items-center rounded-full group"
            >
              <span className="h-7 w-7 grid place-items-center rounded-full
                               bg-white/80 text-arena-800 border border-arena-300/70 shadow-md backdrop-blur
                               dark:bg-arena-900/70 dark:text-neon-100 dark:border-neon-400/30
                               group-active:scale-90 transition">
                <Icon name={icon} size={16} />
              </span>
            </button>
          ))}
        </div>
      </section>

      {lockedModal && <LockedGameModal game={lockedModal} onClose={() => setLockedModal(null)} />}
    </>
  );
}
